import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { VideoCard } from '../components/VideoCard';
import { featuredAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Play, Palette, Users, Star, Mail } from 'lucide-react';
import { HomePageSEO } from '../components/SEO';
import { leadsAPI } from '../lib/api';
import { toast } from 'sonner';

const testimonials = [
    {
        name: 'Michael R.',
        location: 'Portland, OR',
        text: 'Tony Pro\'s course completely changed how I approach portrait painting. The way he breaks down flesh tones and value structure finally made it click for me. Worth every penny.',
        rating: 5,
    },
    {
        name: 'Sarah K.',
        location: 'London, UK',
        text: 'I\'ve watched Glen Orbik\'s drawing lessons three times through now. Each time I pick up something new. This is the closest thing to a real atelier education I\'ve found online.',
        rating: 5,
    },
    {
        name: 'David L.',
        location: 'Austin, TX',
        text: 'The workshop videos are incredible — seeing Jeremy Lipking paint a full portrait from start to finish, hearing his thought process, is something you can\'t get from a book.',
        rating: 5,
    },
];

const LeadMagnetBanner = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const response = await leadsAPI.capture(email);
            setSubmitted(true);
            setEmail('');
            toast.success(response.data.message || 'Check your inbox!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <section className="bg-accent/5 border-y border-accent/20" data-testid="lead-magnet-banner">
                <div className="container-main py-12 text-center">
                    <p className="font-serif text-2xl font-medium text-primary">Thank you! Check your inbox.</p>
                    <p className="text-muted-foreground mt-2 text-sm">Your free drawing principles guide is on its way.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-accent/5 border-y border-accent/20" data-testid="lead-magnet-banner">
            <div className="container-main py-12">
                <div className="max-w-2xl mx-auto text-center">
                    <span className="text-xs font-semibold uppercase tracking-widest text-accent">Free guide</span>
                    <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mt-3">
                        Free: 10 Drawing Principles from Fred Fixler (Reilly Master)
                    </h2>
                    <p className="text-muted-foreground mt-3 text-sm max-w-lg mx-auto">
                        Foundational principles from one of Frank Reilly's most respected students — on value structure, edge control, and constructing a drawing that holds together.
                    </p>
                    <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="email"
                                required
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                                data-testid="lead-magnet-email"
                            />
                        </div>
                        <Button type="submit" disabled={loading} size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="lead-magnet-submit">
                            {loading ? 'Sending...' : 'Get Free Guide'}
                        </Button>
                    </form>
                    <p className="text-xs text-muted-foreground/70 mt-3">No spam. Unsubscribe anytime.</p>
                </div>
            </div>
        </section>
    );
};

export const HomePage = () => {
    const { isAuthenticated } = useAuth();
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFeatured = async () => {
            try {
                const response = await featuredAPI.getFeatured();
                setFeatured(response.data.featured || []);
            } catch (err) {
                console.error('Failed to fetch featured content:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchFeatured();
    }, []);

    return (
        <div data-testid="home-page">
            <HomePageSEO />
            {/* Hero Section */}
            <section data-testid="hero-section">
                {/* Hero Image with Animated Logo */}
                <div className="relative w-full h-[400px] overflow-hidden">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_video-rebuild-2/artifacts/lhup1253_Glen4K.jpg"
                        alt="Glen Orbik teaching drawing"
                        className="w-full h-full object-cover object-center"
                    />
                    {/* Animated Logo Overlay - Left Aligned */}
                    <div className="absolute inset-0 flex items-center justify-start pl-8 md:pl-16 lg:pl-24">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_oilpaint-edu/artifacts/k884xqpf_Zarolla_logo_final_orange_nosub.png"
                            alt="Zarolla"
                            className="h-24 md:h-40 lg:h-48 w-auto animate-fade-in-left drop-shadow-2xl"
                            style={{
                                filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))'
                            }}
                        />
                    </div>
                </div>
                
                {/* Copy below image */}
                <div className="container-main py-16">
                    <div className="max-w-2xl mx-auto text-center">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Professional Art Education
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Master Drawing and
                            <span className="block text-accent">Painting Techniques</span>
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-lg mx-auto">
                            Learn from master artists like Tony Pro, Jeremy Lipking, and Glen Orbik through professional video instruction. 
                            Portrait painting, figure drawing, alla prima techniques, and more.
                        </p>
                        <p className="text-sm text-muted-foreground/80 mt-3 tracking-wide">
                            Lifetime access &middot; Learn at your own pace &middot; Master artist instruction
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 mt-8">
                            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8 py-6" data-testid="hero-browse-courses">
                                <Link to="/courses">
                                    Browse Courses
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="text-base px-8 py-6" data-testid="hero-view-workshops">
                                <Link to="/workshops">
                                    View Painting Videos
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Content */}
            {featured.length > 0 && (
                <section className="section-gap bg-secondary/30" data-testid="featured-section">
                    <div className="container-main">
                        <div className="flex items-end justify-between mb-8">
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                    Start learning today
                                </span>
                                <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mt-2">
                                    Featured Courses
                                </h2>
                            </div>
                            <Link 
                                to="/courses" 
                                className="text-sm font-medium text-accent hover:underline flex items-center gap-1"
                                data-testid="view-all-courses"
                            >
                                View All <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {featured.map((item) => (
                                <VideoCard 
                                    key={item.id} 
                                    video={item} 
                                    type={item.type === 'course' ? 'course' : 'video'} 
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Testimonials Section */}
            <section className="section-gap" data-testid="testimonials-section">
                <div className="container-main">
                    <div className="text-center mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Student reviews
                        </span>
                        <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mt-2">
                            What Our Students Say
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((t, i) => (
                            <div key={i} className="bg-secondary/30 rounded-sm p-8" data-testid={`testimonial-${i}`}>
                                <div className="flex gap-0.5 mb-4">
                                    {Array.from({ length: t.rating }).map((_, j) => (
                                        <Star key={j} className="h-4 w-4 fill-accent text-accent" />
                                    ))}
                                </div>
                                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                                    "{t.text}"
                                </p>
                                <div>
                                    <p className="font-medium text-sm text-primary">{t.name}</p>
                                    <p className="text-xs text-muted-foreground">{t.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lead Magnet Banner */}
            <LeadMagnetBanner />

            {/* Features Section */}
            <section className="py-8" data-testid="features-section">
                <div className="container-main">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center p-6">
                            <div className="w-14 h-14 mx-auto rounded-full bg-accent/10 flex items-center justify-center mb-4">
                                <Play className="h-6 w-6 text-accent" />
                            </div>
                            <h3 className="font-serif text-xl font-medium mb-2">Professional Videos</h3>
                            <p className="text-muted-foreground text-sm">
                                High-quality video instruction from master artists
                            </p>
                        </div>
                        <div className="text-center p-6">
                            <div className="w-14 h-14 mx-auto rounded-full bg-accent/10 flex items-center justify-center mb-4">
                                <Palette className="h-6 w-6 text-accent" />
                            </div>
                            <h3 className="font-serif text-xl font-medium mb-2">Diverse Techniques</h3>
                            <p className="text-muted-foreground text-sm">
                                Alla prima, portraiture, plein air, and sculpting
                            </p>
                        </div>
                        <div className="text-center p-6">
                            <div className="w-14 h-14 mx-auto rounded-full bg-accent/10 flex items-center justify-center mb-4">
                                <Users className="h-6 w-6 text-accent" />
                            </div>
                            <h3 className="font-serif text-xl font-medium mb-2">Expert Instruction</h3>
                            <p className="text-muted-foreground text-sm">
                                Learn from Jeremy Lipking, Tony Pro, and more
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            {!isAuthenticated && (
                <section className="section-gap bg-primary text-primary-foreground" data-testid="cta-section">
                    <div className="container-main text-center">
                        <h2 className="font-serif text-3xl md:text-4xl font-medium">
                            Ready to Start Learning?
                        </h2>
                        <p className="text-primary-foreground/70 mt-4 max-w-2xl mx-auto">
                            Sign in to access your purchased content or browse our catalog 
                            of professional art instruction videos.
                        </p>
                        <Button 
                            asChild 
                            size="lg" 
                            className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90"
                            data-testid="cta-sign-in"
                        >
                            <Link to="/login">
                                Sign In with Email
                            </Link>
                        </Button>
                    </div>
                </section>
            )}
        </div>
    );
};

export default HomePage;
